import {HLine, SettingsSectionContent, SettingsSectionTitle,} from "widgets/profile/ui/styles.ts";
import {FC, useEffect, useMemo} from "react";
import {useApiariesStore} from "entities/apiaries";
import {IDictionary} from "shared/types";
import {
  DictionaryEntre,
  DictionaryEntreDesc,
  DictionaryEntreTitle,
  DictionaryWrapper,
  LetterContainer,
  LetterTitle,
} from "widgets/profile/ui/ui/style.ts";
import {EditIcon} from "shared/ui/icons/editIcon.tsx";
import {useDictionariesStore} from "entities/dictionaries";
import {useResize} from "../../../../app/lib/hooks/useResize.ts";
import {Button} from "@atomaro/ui-kit";
import {AddLarge} from "shared/ui/icons/addLarge.tsx";

type Group = { key: string; items: IDictionary[] };

export const DictionariesContent: FC<{
  dictionaryType: IDictionary;
}> = ({ dictionaryType }) => {
  const currentApiaryId = useApiariesStore((s) => s.currentApiaryId);
  const dictionaries = useDictionariesStore((s) => s.dictionaries);
  const getDictionariesFromServer = useDictionariesStore(
    (s) => s.getDictionariesFromServer
  );
  const openEditModal = useDictionariesStore((s) => s.openEditModal);

  const dictionary = useMemo(() => {
    return dictionaries.find((e) => e.type === dictionaryType.type);
  }, [dictionaryType, dictionaries]);

  useEffect(() => {
    if (!currentApiaryId) return;
    getDictionariesFromServer(String(currentApiaryId));
  }, [currentApiaryId, getDictionariesFromServer]);

  const groups: Group[] = useMemo(() => {
    const typeKey = String(
      (dictionary as any)?.apiId ?? (dictionary as any)?.type ?? ""
    );
    const collator = new Intl.Collator("ru-RU");
    const filtered = dictionaries
      .filter((it) => String((it as any)?.type ?? "") === typeKey)
      .map((it) => ({
        ...it,
        name: String((it as any)?.name ?? "").trim(),
        description: String((it as any)?.description ?? "").trim(),
      }))
      .filter((it) => it.name.length > 0)
      .sort((a, b) => collator.compare(a.name, b.name));

    const map = new Map<string, IDictionary[]>();
    for (const it of filtered) {
      const first = (it.name[0] || "").toLocaleUpperCase("ru-RU");
      const key = /\p{N}/u.test(first) ? "0-9" : first;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(it);
    }

    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === "0-9" && b !== "0-9") return -1;
      if (b === "0-9" && a !== "0-9") return 1;
      return collator.compare(a, b);
    });

    return keys.map((k) => ({
      key: k,
      items: map.get(k)!.sort((a, b) => collator.compare(a.name, b.name)),
    }));
  }, [dictionaries, dictionary]);

  const isScreenMd = useResize();

  return (
    <SettingsSectionContent>
      {isScreenMd.isScreenMd && (
        <>
          <SettingsSectionTitle>{dictionary?.name}</SettingsSectionTitle>
          <HLine />
        </>
      )}
      <DictionaryWrapper>
        {groups.map((g) => (
          <LetterContainer key={g.key}>
            <LetterTitle>{g.key}</LetterTitle>
            {g.items.map((el) => (
              <DictionaryEntre
                key={String((el as any)?.id ?? el.name)}
                style={{ marginBottom: 16 }}
              >
                <DictionaryEntreTitle>{el.name}</DictionaryEntreTitle>
                <DictionaryEntreDesc>
                  {el.description ? el.description : "Нет описания"}
                </DictionaryEntreDesc>
                  <div
                    className={"edit-button"}
                    onClick={() => openEditModal(el.id, "EDIT")}
                  >
                    <EditIcon />
                  </div>
              </DictionaryEntre>
            ))}
          </LetterContainer>
        ))}
        {isScreenMd.isScreenMd ? (
          <Button
            label="Новый термин"
            colorScheme="neutral"
            size="m"
            variant="outline"
            iconPrefix={<AddLarge fill={"#585D69"} />}
            style={{
              width: "100%",
            }}
            onClick={() => openEditModal(dictionary?.type, "ADD")}
          />
        ) : (
          <Button
            colorScheme="accent"
            size="l"
            variant="primary"
            iconPrefix={<AddLarge fill={"#ffff"} />}
            style={{
              position: "fixed",
              bottom: "81px",
              right: "16px",
            }}
            onClick={() => openEditModal(dictionary?.type, "ADD")}
          />
        )}
      </DictionaryWrapper>
    </SettingsSectionContent>
  );
};

export default DictionariesContent;
